import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Share2, Copy, ExternalLink, Check, Eye } from "lucide-react";
import { toast } from "sonner";

const SHARE_FIELDS = [
  ["photo", "rbPhoto"], ["address", "rbAddress"], ["phone", "rbPhone"],
  ["email", "rbEmail"], ["linkedin", "rbLinkedin"],
  ["birthdate", "ppBirthdate"], ["nationality", "rbNationality"],
];

export default function ShareCvCard() {
  const { t } = useT();
  const [s, setS] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/share/settings"), api.get("/resumes")])
      .then(([a, b]) => {
        const settings = a.data;
        if (!settings.resume_id && b.data.length) {
          const def = b.data.find((r) => r.is_default) || b.data[0];
          settings.resume_id = def.id;
        }
        setS(settings);
        setResumes(b.data);
      })
      .catch(() => {});
  }, []);

  if (!s) return null;

  const link = `${window.location.origin}/cv/${s.slug}`;

  const save = async (patch = {}) => {
    const next = { ...s, ...patch };
    setBusy(true);
    try {
      const { data } = await api.put("/share/settings", {
        slug: next.slug, resume_id: next.resume_id,
        enabled: next.enabled, shared_fields: next.shared_fields || [],
      });
      setS({ ...next, ...data });
      toast.success(next.enabled ? t("shareSaved") : t("shareDisabled"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("shareCopied"));
    } catch {
      toast.error(link);
    }
  };

  return (
    <section className="mt-12 ed-card p-6" data-testid="share-cv-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="ed-eyebrow flex items-center gap-2">
          <Share2 size={12} strokeWidth={1.5} /> {t("shareTitle")}
        </p>
        <span className="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500">
          <Eye size={11} strokeWidth={1.5} /> {t("shareViews", { n: s.views || 0 })}
        </span>
      </div>
      <p className="text-xs font-mono text-neutral-500 mt-2">{t("shareHint")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5 mt-5">
        <div>
          <label className="ed-label">{t("shareSlugLabel")}</label>
          <div className="flex items-stretch">
            <span className="inline-flex items-center border border-r-0 border-neutral-300 bg-neutral-100 px-3 font-mono text-xs text-neutral-500 whitespace-nowrap">
              /cv/
            </span>
            <input
              data-testid="share-slug-input"
              className="ed-input flex-1"
              value={s.slug}
              onChange={(e) => setS({ ...s, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            />
          </div>
        </div>
        <div>
          <label className="ed-label">{t("shareCvLabel")}</label>
          <select
            data-testid="share-resume-select"
            className="ed-input"
            value={s.resume_id}
            onChange={(e) => setS({ ...s, resume_id: e.target.value })}
          >
            {resumes.map((r) => (
              <option key={r.id} value={r.id}>{r.name}{r.is_default ? " ★" : ""}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <p className="ed-label mb-2">{t("shareFieldsLabel")}</p>
        <div className="flex flex-wrap gap-2" data-testid="share-field-toggles">
          {SHARE_FIELDS.map(([field, labelKey]) => {
            const active = (s.shared_fields || []).includes(field);
            return (
              <button
                key={field}
                data-testid={`share-field-${field}`}
                onClick={() => setS({
                  ...s,
                  shared_fields: active
                    ? (s.shared_fields || []).filter((k) => k !== field)
                    : [...(s.shared_fields || []), field],
                })}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${active ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900"}`}
              >
                <span className={`w-3 h-3 rounded-full border flex items-center justify-center ${active ? "border-[#F9F8F6]" : "border-neutral-400"}`}>
                  {active && <Check size={9} strokeWidth={3} />}
                </span>
                {t(labelKey)}
              </button>
            );
          })}
        </div>
        <p className="text-[0.65rem] font-mono text-neutral-500 mt-2">{t("shareFieldsHint")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-neutral-200">
        {s.enabled ? (
          <>
            <button data-testid="share-save-btn" className="ed-btn" onClick={() => save()} disabled={busy}>
              <Check size={14} strokeWidth={1.5} /> {t("shareSave")}
            </button>
            <button data-testid="share-disable-btn" className="ed-btn ed-btn-outline" onClick={() => save({ enabled: false })} disabled={busy}>
              {t("shareTurnOff")}
            </button>
            <button data-testid="share-copy-btn" className="ed-btn ed-btn-outline" onClick={copy}>
              <Copy size={13} strokeWidth={1.5} /> {t("shareCopy")}
            </button>
            <button
              data-testid="share-copy-social-btn"
              className="ed-btn ed-btn-outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`${window.location.origin}/api/public/cv/${s.slug}/card`);
                  toast.success(t("shareCopied"));
                } catch { toast.error(t("rbSaveFailed")); }
              }}
              title={t("shareCopySocialHint")}
            >
              <Copy size={13} strokeWidth={1.5} /> {t("shareCopySocial")}
            </button>
            <a data-testid="share-open-link" href={link} target="_blank" rel="noreferrer" className="ed-btn ed-btn-outline">
              <ExternalLink size={13} strokeWidth={1.5} /> {t("shareOpen")}
            </a>
          </>
        ) : (
          <button data-testid="share-enable-btn" className="ed-btn" onClick={() => save({ enabled: true })} disabled={busy || resumes.length === 0}>
            <Share2 size={14} strokeWidth={1.5} /> {t("shareEnable")}
          </button>
        )}
        {s.enabled && (
          <span className="w-full sm:w-auto sm:ml-auto font-mono text-xs text-neutral-500 truncate" data-testid="share-link-display">
            {link}
          </span>
        )}
      </div>
    </section>
  );
}
